import React, { Dispatch, SetStateAction } from 'react';
import { Rate, Input } from 'antd';
import { get } from 'lodash';
import intl from 'react-intl-universal';
import cls from 'classnames';

import { Config, QuestionItem } from '@src/apps/interview-questionnaire/services/model';
import { ReactComponent as Star } from '@svg/icon-star.svg';
import { FormItem } from '../../FormItem';

import './index.less';

export interface RateItemProps {
    value?: [number, string] | undefined;
    onChange?: (value: [number | undefined, string | undefined] | undefined) => void;
    config: Config;
    id?: string;
    desc?: string;
    onFocus?: (value: any) => void;
    errorMsg?: string;
    resetError?: () => void;
    childList?: QuestionItem[];
    errorList?: { [key: string]: string };
    setErrorList?: Dispatch<SetStateAction<{ [key: string]: string }>>;
}

export const validatorGenerate = (config: Config) => {
    const require_reason = get(config, 'five_star_scoring.require_reason', []);
    return {
        validator: async (_, value: [number, string] | undefined) => {
            const [score, specificReason] = value || [];
            const isRequired = score && require_reason[score - 1];

            if (isRequired) {
                return (specificReason || '').trim()
                    ? Promise.resolve()
                    : Promise.reject(intl.get('reasonRequiredError'));
            }
        }
    };
};

const { useRef } = React;
const RateItem: React.FC<RateItemProps> = ({
    value,
    onChange,
    config,
    id,
    desc,
    errorMsg,
    resetError,
    childList,
    ...props
}) => {
    const specificRreasonRef = useRef<string>();
    const [score, specificReason] = value || [];
    const max_rate_desc = get(config, 'five_star_scoring.max_score_desc.i18n_name', '');
    const min_rate_desc = get(config, 'five_star_scoring.min_score_desc.i18n_name', '');
    const require_reason = get(config, 'five_star_scoring.require_reason', []);
    const reason_desc = get(config, 'five_star_scoring.reason_desc', []);
    const showInput = score && require_reason[score - 1];
    const InputPlaceholder = score && get(reason_desc, `${score - 1}.i18n_name`, '');
    const isReasonError = errorMsg === intl.get('reasonRequiredError');

    const handleRateChange = (val: number) => {
        resetError && resetError();
        onChange &&
            onChange([val, val && require_reason[val - 1] ? specificRreasonRef.current : '']);
    };
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        resetError && resetError();
        const value = e.target.value;
        specificRreasonRef.current = value;
        onChange && onChange([score, value]);
    };

    const renderChildren = () => {
        if (childList?.length && childList?.length > 0) {
            return childList?.map((item) => {
                const children_item_require_reason = get(
                    item,
                    'type.config.five_star_scoring.require_reason'
                );
                // 二级题目分组展示
                if (children_item_require_reason) {
                    if (score && children_item_require_reason[score - 1]) {
                        return (
                            <FormItem
                                item={item}
                                key={item.object_id}
                                errorList={props.errorList}
                                setErrorList={props.setErrorList}
                                isChild={true}
                                ruleCheckVisible
                            />
                        );
                    }
                } else {
                    return (
                        <FormItem
                            item={item}
                            key={item.object_id}
                            errorList={props.errorList}
                            setErrorList={props.setErrorList}
                            isChild={true}
                            ruleCheckVisible
                        />
                    );
                }
            });
        } else {
            return (
                <Input
                    placeholder={InputPlaceholder}
                    value={specificReason}
                    onChange={handleInputChange}
                    className="questionnaire__form__rate__input"
                />
            );
        }
    };

    return (
        <div
            className={cls('questionnaire__form__rate-wrapper', {
                'questionnaire__form__rate-wrapper--has-after-explain': isReasonError && errorMsg
            })}
            id={id}
        >
            {desc && <div className="desc">{desc}</div>}
            {!isReasonError && errorMsg && <div className="error-explain">{errorMsg}</div>}
            <div
                className={cls('questionnaire__form__rate-content', {
                    'questionnaire__form__rate-content--selected': !!score
                })}
                onMouseEnterCapture={(e) => score && e.stopPropagation()}
                onMouseOverCapture={(e) => score && e.stopPropagation()}
                onMouseMoveCapture={(e) => score && e.stopPropagation()}
                onMouseLeaveCapture={(e) => score && e.stopPropagation()}
            >
                <span className="min_rate_desc">{min_rate_desc}</span>
                <Rate
                    character={<Star />}
                    value={score}
                    onChange={handleRateChange}
                    allowClear={false}
                    {...props}
                />
                <span className="max_rate_desc ">{max_rate_desc}</span>
            </div>
            {showInput && renderChildren()}
            {isReasonError && errorMsg && <div className="error-explain--after">{errorMsg}</div>}
        </div>
    );
};

export default RateItem;
